import { useEffect, useRef, useState } from 'react';

/**
 * Sprint 7.3: input numérico que SÍ permite escribir.
 *
 * Por qué no usar `<input type="number">`:
 *   - En navegadores Chromium los spinners ↑↓ ocupan espacio del input y
 *     en modales con `w-20` (80px) cubren casi toda la zona escribible.
 *   - El scroll de la rueda cambia el valor inadvertidamente (vector de bug
 *     contable: el dueño cambia "$500" a "$501" sin querer al hacer scroll).
 *   - En táctil, `inputMode="decimal"` da el mismo teclado numérico sin los
 *     spinners.
 *
 * Comportamiento:
 *   - Valor controlado (`value: number`) y `onChange(n: number)`.
 *   - Mantiene un buffer string interno mientras el usuario edita: permite
 *     estados transitorios como "" o "12." sin reescribir el cursor.
 *   - Sanitiza: solo dígitos, un único punto, signo opcional. Letras se
 *     descartan en `onChange` antes de llegar a estado.
 *   - Onblur: clamp a `min`/`max` si están definidos, redondea a `decimals`.
 *   - Sprint 30: al recibir foco se selecciona TODO el contenido. Así, si el
 *     campo trae un valor por defecto (ej. "0"), lo que el usuario teclee lo
 *     reemplaza — imposible concatenar dígitos por error (bug del "750"→"7500").
 */

interface Props {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  decimals?: number;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  ariaLabel?: string;
  allowEmpty?: boolean; // Si true, "" se reporta como 0 sin clamp.
  autoFocus?: boolean;
}

const ALLOWED = /^-?\d*\.?\d*$/;

function formatear(n: number, decimals: number): string {
  if (!Number.isFinite(n)) return '0';
  return decimals > 0 ? n.toFixed(decimals).replace(/\.?0+$/, '') || '0' : String(Math.round(n));
}

export function NumberField({
  value,
  onChange,
  min,
  max,
  decimals = 2,
  placeholder,
  className,
  id,
  disabled,
  ariaLabel,
  allowEmpty,
  autoFocus,
}: Props) {
  const [buffer, setBuffer] = useState<string>(() => formatear(value, decimals));
  const ultimoValor = useRef<number>(value);

  // Sincroniza buffer cuando el value cambia desde fuera (ej: reset del form).
  useEffect(() => {
    if (value !== ultimoValor.current) {
      setBuffer(formatear(value, decimals));
      ultimoValor.current = value;
    }
  }, [value, decimals]);

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' || ALLOWED.test(raw)) {
      setBuffer(raw);
      // Reportar el número sanitizado mientras se escribe (sin clamp para no
      // saltar el cursor). El clamp ocurre en onBlur.
      const n = parseFloat(raw);
      if (Number.isFinite(n)) {
        ultimoValor.current = n;
        onChange(n);
      } else if (allowEmpty && raw === '') {
        ultimoValor.current = 0;
        onChange(0);
      }
    }
  };

  const onBlur = () => {
    let n = parseFloat(buffer);
    if (!Number.isFinite(n)) n = 0;
    if (typeof min === 'number' && n < min) n = min;
    if (typeof max === 'number' && n > max) n = max;
    if (decimals >= 0) {
      const factor = Math.pow(10, decimals);
      n = Math.round(n * factor) / factor;
    }
    ultimoValor.current = n;
    setBuffer(formatear(n, decimals));
    onChange(n);
  };

  return (
    <input
      id={id}
      type="text"
      inputMode={decimals > 0 ? 'decimal' : 'numeric'}
      value={buffer}
      onChange={onInput}
      onBlur={onBlur}
      onFocus={(e) => e.currentTarget.select()}
      onWheel={(e) => (e.target as HTMLElement).blur()}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      aria-label={ariaLabel}
      autoFocus={autoFocus}
      autoComplete="off"
      spellCheck={false}
    />
  );
}
